import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore, type LoginResponse } from '../store/authStore'
import {
  login, loginMFA, loginMFASetup, loginMFAEnable, loginPasswordChange,
  isMFAChallenge, isMFAEnrollment, isPasswordChangeRequired,
  checkSSODomain, initiateSSOLogin,
  type LoginOutcome, type PasswordChangeHold,
} from '../services/api'
import { errorText } from '../utils/errorText'
import { passwordRefusal, unmetRules } from '../utils/passwordPolicy'
import MicrosoftSignInButton from '../components/MicrosoftSignInButton'
import MfaSetupModal from '../components/MfaSetupModal'
import PasswordRules from '../components/PasswordRules'
import { useCookieConsent } from '../hooks/useCookieConsent'
import { isMicrosoftIdP } from '../utils/sso'

export default function Login() {
  const navigate = useNavigate()
  const { login: setAuth } = useAuthStore()
  useCookieConsent()
  const [credentials, setCredentials] = useState({ email: '', password: '' })
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [ssoLoading, setSSOLoading] = useState(false)

  // SSO availability
  const [ssoAvailable, setSSOAvailable] = useState(false)
  const [ssoRequired, setSSORequired] = useState(false)
  const [ssoLabel, setSSOLabel] = useState('SSO')
  // Microsoft asks that an Entra-backed sign-in use their button and wording rather than a generic one.
  const [ssoIsMicrosoft, setSSOIsMicrosoft] = useState(false)
  const domainCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Explicit login method: the user picks, rather than the page guessing from the email
  const [method, setMethod] = useState<'password' | 'sso'>('password')

  // The second factor. `mfaToken` is what the password step earned: it is not a credential, it
  // only says the password was right and buys a few minutes to produce a code.
  const [mfaToken, setMFAToken] = useState<string | null>(null)
  const [mfaCode, setMFACode] = useState('')

  // Enrolment required by the organization. `enrollToken` is the same kind of token as
  // `mfaToken`; `pendingSession` is the sign-in the enable step returned, held back until the
  // recovery codes have been acknowledged so they cannot be navigated away from unseen.
  const [enrollToken, setEnrollToken] = useState<string | null>(null)
  const [pendingSession, setPendingSession] = useState<LoginResponse | null>(null)

  // The password was right but no longer meets the organization's rules: the sign-in is held
  // until a compliant one is chosen here, then continues into whichever step applies next.
  const [passwordHold, setPasswordHold] = useState<PasswordChangeHold | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Every sign-in answer, from the password step or from a hold, lands here: a session is
  // stored and the app opens; a hold swaps the form for its own step.
  const handleOutcome = (response: LoginOutcome) => {
    setPasswordHold(null)
    if (isPasswordChangeRequired(response)) {
      setPasswordHold(response)
      setNewPassword('')
      setConfirmPassword('')
      return
    }
    if (isMFAChallenge(response)) {
      setMFAToken(response.mfa_token)
      setMFACode('')
      return
    }
    if (isMFAEnrollment(response)) {
      setEnrollToken(response.mfa_token)
      setPendingSession(null)
      return
    }
    setAuth(response)
    navigate('/')
  }

  // Check SSO on page load (catches configs with no domain restriction)
  useEffect(() => {
    checkSSODomain('check@any.domain').then(result => {
      if (result.sso_available) {
        setSSOAvailable(true)
        setSSORequired(result.sso_required)
        setSSOLabel(result.display_name || result.protocol?.toUpperCase() || 'SSO')
        setSSOIsMicrosoft(isMicrosoftIdP(result))
      }
    }).catch(() => {})
  }, [])

  // Re-check SSO when email changes (catches domain-specific configs)
  useEffect(() => {
    if (domainCheckTimer.current) clearTimeout(domainCheckTimer.current)

    const email = credentials.email
    if (!email || !email.includes('@') || email.endsWith('@')) return

    domainCheckTimer.current = setTimeout(async () => {
      try {
        const result = await checkSSODomain(email)
        // Every field is rewritten, including on a miss: a corrected typo must not leave the
        // previous domain's answer on screen -- Microsoft's button in front of another IdP,
        // or a password tab locked out by a domain the user is no longer signing in as.
        setSSOAvailable(result.sso_available)
        setSSORequired(result.sso_available && result.sso_required)
        setSSOLabel(result.display_name || result.protocol?.toUpperCase() || 'SSO')
        setSSOIsMicrosoft(result.sso_available && isMicrosoftIdP(result))
        // An SSO-required domain has no password path to offer
        if (result.sso_available && result.sso_required) setMethod('sso')
      } catch {}
    }, 400)

    return () => {
      if (domainCheckTimer.current) clearTimeout(domainCheckTimer.current)
    }
  }, [credentials.email])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      handleOutcome(await login(credentials.email, credentials.password))
    } catch (err: any) {
      setError(errorText(err, 'Login failed. Please check your credentials.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!passwordHold) return
    setError(null)
    const unmet = unmetRules(newPassword, passwordHold.password_policy)
    if (unmet.length) {
      setError(passwordRefusal(unmet))
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setIsLoading(true)
    try {
      handleOutcome(await loginPasswordChange(passwordHold.password_token, newPassword))
    } catch (err: any) {
      setError(errorText(err, 'That password was not accepted. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  const cancelPasswordChange = () => {
    setPasswordHold(null)
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
    setCredentials({ ...credentials, password: '' })
  }

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaToken) return
    setError(null)
    setIsLoading(true)
    try {
      const response = await loginMFA(mfaToken, mfaCode)
      setAuth(response)
      navigate('/')
    } catch (err: any) {
      setError(errorText(err, 'That code was not accepted. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  // Back to the start: the challenge token is dropped rather than kept around unused.
  const cancelMFA = () => {
    setMFAToken(null)
    setMFACode('')
    setError(null)
    setCredentials({ ...credentials, password: '' })
  }

  const cancelEnrollment = () => {
    setEnrollToken(null)
    setPendingSession(null)
    setError(null)
    setCredentials({ ...credentials, password: '' })
  }

  // The enable step is also the sign-in: the session it returns is kept here until the
  // recovery codes are acknowledged, then stored.
  const enrollAndSignIn = async (code: string) => {
    const result = await loginMFAEnable(enrollToken!, code)
    setPendingSession(result)
    return result
  }

  const finishEnrollment = () => {
    if (!pendingSession) return
    setAuth(pendingSession)
    navigate('/')
  }

  const handleSSOLogin = async () => {
    if (!credentials.email || !credentials.email.includes('@')) {
      setError('Please enter your email address first')
      return
    }
    setError(null)
    setSSOLoading(true)
    try {
      const result = await initiateSSOLogin(credentials.email)
      window.location.href = result.auth_url
    } catch (err: any) {
      setError(errorText(err, 'Failed to initiate SSO login'))
      setSSOLoading(false)
    }
  }

  const passwordAllowed = !ssoRequired
  const showPasswordForm = method === 'password' && passwordAllowed

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
        <div className="flex items-center justify-center space-x-3 mb-6">
          <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
          <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* A new password is required. The password step is done; the organization's rules
            refuse the password it verified, so a compliant one is chosen before anything else. */}
        {passwordHold ? (
          <form onSubmit={handlePasswordChangeSubmit} className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-theme-text-primary">Choose a new password</h3>
              <p className="mt-1 text-sm text-theme-text-secondary">
                Your organization's password rules have changed, and your current password does not
                meet them ({passwordHold.unmet.map((r) => r.charAt(0).toLowerCase() + r.slice(1)).join('; ')}).
                Choose a new one to continue.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-1">New password</label>
              <input
                type="password"
                required
                autoFocus
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                aria-label="New password"
              />
              <PasswordRules password={newPassword} policy={passwordHold.password_policy} />
            </div>
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-1">Confirm new password</label>
              <input
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                aria-label="Confirm new password"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !newPassword || !confirmPassword}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Saving...' : 'Save and continue'}
            </button>
            <button
              type="button"
              onClick={cancelPasswordChange}
              className="w-full text-sm text-theme-text-secondary hover:text-theme-text-primary"
            >
              Use a different account
            </button>
          </form>
        ) : enrollToken ? (
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-theme-text-primary">Two-factor authentication required</h3>
              <p className="mt-1 text-sm text-theme-text-secondary">
                Your organization requires two-factor authentication for password sign-ins. Set up
                an authenticator app to continue.
              </p>
            </div>
            <button
              type="button"
              onClick={cancelEnrollment}
              className="w-full text-sm text-theme-text-secondary hover:text-theme-text-primary"
            >
              Use a different account
            </button>
            <MfaSetupModal
              open
              onClose={cancelEnrollment}
              setup={() => loginMFASetup(enrollToken)}
              enable={enrollAndSignIn}
              onEnabled={() => {}}
              onDone={finishEnrollment}
              intro={
                <p className="text-sm text-theme-text-secondary">
                  This takes a minute. The sign-in you started stays good for five minutes; if it
                  runs out, sign in again to get a fresh code.
                </p>
              }
            />
          </div>
        ) : mfaToken ? (
          <form onSubmit={handleMFASubmit} className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-theme-text-primary">Two-factor authentication</h3>
              <p className="mt-1 text-sm text-theme-text-secondary">
                Enter the 6-digit code from your authenticator app, or one of your recovery codes.
              </p>
            </div>
            <input
              type="text"
              required
              autoFocus
              // Not `type="number"`: a recovery code has letters and a dash, and the same field
              // takes both. inputMode keeps the numeric keypad up on a phone for the common case.
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMFACode(e.target.value)}
              className="block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 text-center text-lg tracking-widest focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="000000"
              aria-label="Verification code"
            />
            <button
              type="submit"
              disabled={isLoading || mfaCode.trim().length < 6}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={cancelMFA}
              className="w-full text-sm text-theme-text-secondary hover:text-theme-text-primary"
            >
              Use a different account
            </button>
          </form>
        ) : (
        <>
        {/* Explicit login-method choice */}
        <div className="flex space-x-1 bg-theme-tertiary rounded-lg p-1 mb-4">
          {([
            { value: 'password' as const, label: 'Password' },
            { value: 'sso' as const, label: 'Single sign-on' },
          ]).map(tab => (
            <button
              key={tab.value}
              type="button"
              disabled={tab.value === 'password' && !passwordAllowed}
              onClick={() => { setMethod(tab.value); setError(null) }}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                method === tab.value
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-theme-text-secondary hover:text-theme-text-primary'
              } disabled:opacity-60`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">Email</label>
            <input
              type="email"
              required
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
              placeholder="Enter your email"
            />
          </div>

          {showPasswordForm && (
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-1">Password</label>
              <input
                type="password"
                required
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter your password"
              />
            </div>
          )}

          {showPasswordForm && (
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          )}

          {/* Password tab shortcut once the email's domain is known to have SSO */}
          {showPasswordForm && ssoAvailable && (
            <p className="text-xs text-center text-theme-text-secondary">
              {ssoIsMicrosoft
                ? 'Your organization supports signing in with a work or school account — use the Single sign-on tab'
                : `Your organization supports ${ssoLabel} — use the Single sign-on tab`}
            </p>
          )}

          {method === 'sso' && (
            <>
              {ssoIsMicrosoft ? (
                // Microsoft's button carries its own label, so the progress and the
                // "work or school account" wording they ask for go in the text below it.
                <div className="flex justify-center">
                  <MicrosoftSignInButton onClick={handleSSOLogin} disabled={ssoLoading} />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleSSOLogin}
                  disabled={ssoLoading}
                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                  </svg>
                  <span>
                    {ssoLoading
                      ? 'Redirecting...'
                      : ssoAvailable
                        ? `Continue with ${ssoLabel}`
                        : 'Continue with single sign-on'}
                  </span>
                </button>
              )}
              <p className="text-xs text-center text-theme-text-secondary">
                {ssoIsMicrosoft
                  ? ssoLoading
                    ? 'Redirecting to Microsoft...'
                    : ssoRequired
                      ? 'Your organization requires you to sign in with your work or school account'
                      : 'Use your work or school account'
                  : ssoRequired
                    ? 'Your organization requires SSO login'
                    : "You'll be redirected to your organization's identity provider"}
              </p>
            </>
          )}
        </form>

        <div className="mt-4 text-center">
          <Link to="/forgot-password" className="text-sm text-blue-500 hover:text-blue-400">
            Forgot your password?
          </Link>
        </div>

        <div className="mt-4 text-center text-sm text-theme-text-secondary">
          Don't have an account?{' '}
          <Link to="/signup" className="text-blue-500 hover:text-blue-400">Sign up</Link>
        </div>
        </>
        )}
      </div>
    </div>
  )
}
